import * as React from 'react';
import tw from 'twin.macro';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';

import { LinkBase, Title } from '@restworld/ui-ds';
import { LocalityType, PopularLocality } from '@restworld/data-services';
import { customImageLoader, getAssetSrc } from '@restworld/utils-common';

interface ITopLocalitesProps {
  redirectToPath: (path: string, query?: { [key: string]: string | boolean }) => void;
  popularLocalities: PopularLocality[];
}
export const TopLocalites: React.FunctionComponent<ITopLocalitesProps> = ({
  popularLocalities,
}: ITopLocalitesProps) => {
  const { t: homePageTranslate } = useTranslation('home-page');
  const topLocalities = React.useMemo(
    () =>
      popularLocalities.map(({ locality, locality_type }, index) => (
        <li key={index}>
          <div tw="rounded-2xl p-6 w-full min-w-[23rem] bg-primary-50">
            <div tw="flex flex-row gap-4">
              <div tw="bg-white rounded-lg flex items-center justify-center h-12 w-12">
                <Image
                  src={getLocalityIcon(locality_type)}
                  width={32}
                  height={32}
                  alt={locality}
                  loader={customImageLoader}
                />
              </div>
              <div tw="flex flex-col gap-2">
                <Title content={locality} fontSize="superLarge" fontWeight="semibold" tw="leading-9" />
                <div tw="w-full text-left">
                  <LinkBase link={`/cerco-lavoro/${locality.toLowerCase().replace(/ /g, '_')}`}>
                    <Title
                      content={homePageTranslate('discover_open_positions')}
                      textColor="bold-purple"
                      suffixIcon="ArrowLongRightPurpleIcon"
                      isPointer
                      fontWeight="semibold"
                      tw="leading-6"
                      fontSize="base"
                    />
                  </LinkBase>
                </div>
              </div>
            </div>
          </div>
        </li>
      )),
    [homePageTranslate, popularLocalities]
  );
  return (
    <div tw="flex flex-col gap-8 py-20">
      <div tw="flex flex-col gap-2 text-start">
        <Title
          content={`${homePageTranslate('where_is_the_limit')} 🚀`}
          fontSize="extraLarge"
          fontWeight="medium"
          twStyle={tw`leading-13.5`}
          textColor="gray"
        />
        <Title
          content={homePageTranslate('top_city_for_opportunities')}
          fontSize="text-4xl"
          fontWeight="semibold"
          twStyle={tw`leading-13.5`}
        />
        <Title
          content={homePageTranslate('job_in_city')}
          fontSize="large"
          fontWeight="normal"
          twStyle={tw`leading-6.75`}
        />
      </div>
      <ul tw="grid 2xl:grid-cols-3  lg:grid-cols-2 md:grid-cols-1  grid-cols-1  gap-4">{topLocalities}</ul>
    </div>
  );
};

export const getLocalityIcon = (localityType: LocalityType) => {
  let localityUrl = getAssetSrc('/utils-assets/city.svg');
  if (localityType === 'sea') {
    localityUrl = getAssetSrc('/utils-assets/sea.svg');
  }
  if (localityType === 'mountain') {
    localityUrl = getAssetSrc('/utils-assets/mountain.svg');
  }
  return localityUrl;
};

export default TopLocalites;
